import {
  isLatin1,
  MAX_REFERENCES,
  REFERENCE_DESCRIPTION_MAX,
  REFERENCE_URL_MAX,
  type UrlReference,
  type UrlReferenceIssue,
} from './xref01Validation';

/**
 * Extensions the XML referral attachment picker accepts.
 * Spor A/Referencer/Schemas/Konverter/Konverter/xml/Schemas/2017/05/01/GeneralTypes.xsd
 * permits these, and MedCom (Standardteam) confirmed the pairings in writing on 2026-09-29.
 * `zip` (BIN01-S 3.3, file18.ZIP): XBIN02 p. 23 says zip "kan kvalificeres med alle
 * ObjectCode", so the clinician names what the archive holds (`ZIP_OBJECT_TYPES`) and the
 * gateway gets it as `objectType`.
 */
export const XML_ATTACHMENT_EXTENSIONS = [
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'tif',
  'tiff',
  'bmp',
  'wav',
  'scp',
  'gif',
  'txt',
  'rtf',
  'doc',
  'xls',
  'dcm',
  'zip',
] as const;

/**
 * What a zip file may hold, as OBJEKTTYPE (sent as `objectType`) with its XML ObjectCode.
 * proprietaert_indhold is for bilateral agreements only and program is not sent, so
 * neither is offered.
 */
export const ZIP_OBJECT_TYPES = ['TXT', 'IMG', 'VGR', 'BSG', 'MUL'] as const;
export type ZipObjectType = (typeof ZIP_OBJECT_TYPES)[number];

export function isZipObjectType(value: string): value is ZipObjectType {
  return (ZIP_OBJECT_TYPES as readonly string[]).includes(value);
}

/** True when the file is a zip and so needs a named content type before upload. */
export function needsZipObjectType(fileName: string): boolean {
  return referralFileExtension(fileName) === 'zip';
}

/** `objectType` for the communicate-medbin call: only a zip carries one. */
export function medbinObjectType(fileName: string, zipObjectType: ZipObjectType | undefined): ZipObjectType | undefined {
  return needsZipObjectType(fileName) ? zipObjectType : undefined;
}

export type XmlAttachmentExtension = (typeof XML_ATTACHMENT_EXTENSIONS)[number];

export const XML_ATTACHMENT_ACCEPT = XML_ATTACHMENT_EXTENSIONS.map((extension) => `.${extension}`).join(
  ',',
);

/** BIN01 send protocol: at most 10 files, at most 100 MB together. */
export const REFERRAL_FILE_MAX_COUNT = 10;
export const REFERRAL_FILE_MAX_TOTAL_BYTES = 100 * 1024 * 1024;

export function referralFileExtension(fileName: string): string {
  const base = fileName.trim();
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot === base.length - 1) return '';
  return base.slice(dot + 1).toLowerCase();
}

export function isXmlAttachmentExtension(extension: string): extension is XmlAttachmentExtension {
  return (XML_ATTACHMENT_EXTENSIONS as readonly string[]).includes(extension.trim().toLowerCase());
}

export type ReferralFileLimitCode =
  | 'unsupported-type'
  | 'too-many-files'
  | 'file-too-large'
  | 'total-too-large';

export function checkReferralFile(input: {
  fileName: string;
  byteLength: number;
  existingFileCount: number;
  existingTotalBytes: number;
}): { ok: true } | { ok: false; code: ReferralFileLimitCode } {
  if (!isXmlAttachmentExtension(referralFileExtension(input.fileName))) {
    return { ok: false, code: 'unsupported-type' };
  }
  if (!Number.isFinite(input.byteLength) || input.byteLength < 0) {
    return { ok: false, code: 'file-too-large' };
  }
  if (input.byteLength > REFERRAL_FILE_MAX_TOTAL_BYTES) {
    return { ok: false, code: 'file-too-large' };
  }
  if (input.existingFileCount >= REFERRAL_FILE_MAX_COUNT) {
    return { ok: false, code: 'too-many-files' };
  }
  if (input.existingTotalBytes + input.byteLength > REFERRAL_FILE_MAX_TOTAL_BYTES) {
    return { ok: false, code: 'total-too-large' };
  }
  return { ok: true };
}

export type UrlAttachmentDraft = {
  kind: 'url';
  description: string;
  url: string;
};

export type FileAttachmentDraft = {
  kind: 'file';
  description: string;
  fileName: string;
  byteLength: number;
  /** Parent citation: the XBIN object id with the hyphens removed. */
  objectIdentifier: string;
  objectCode: string;
  objectExtensionCode: string;
  originalObjectSize: number | null;
  /** Set for a zip file: what the archive holds (BIN01-S 3.3). */
  zipObjectType?: ZipObjectType;
};

export type AttachmentDraft = UrlAttachmentDraft | FileAttachmentDraft;

export type BinReference = {
  kind: 'bin';
  refDescription: string;
  objectIdentifier: string;
  objectCode: string;
  objectExtensionCode: string;
  originalObjectSize: number;
};

export type ReferralReference = UrlReference | BinReference;

export type ReferralReferenceIssue =
  | (UrlReferenceIssue & { source: 'url' })
  | {
      source: 'file';
      index: number;
      code:
        | 'incomplete'
        | 'latin1'
        | 'unsupported-type'
        | 'not_ready'
        | 'zip_type_missing'
        | 'file-too-large'
        | 'too-many-files'
        | 'total-too-large';
    }
  | { source: 'file'; index: number; code: 'description_long'; length: number; max: number }
  | { source: 'budget'; code: 'too_many' };

function isBlank(row: AttachmentDraft): boolean {
  if (row.kind === 'url') {
    return row.description.trim().length === 0 && row.url.trim().length === 0;
  }
  return row.description.trim().length === 0 && row.fileName.trim().length === 0;
}

function parentObjectId(value: string): string | null {
  const identifier = value.trim();
  if (identifier.length === 0 || identifier.length > 35 || identifier.includes('-')) return null;
  return identifier;
}

/**
 * URL rows and file rows share the 10-reference budget.
 * Blank rows are left out. A file is sent only after the gateway has returned
 * its object codes; until then `requireChecked: false` still reports a chosen
 * file's type and size, and `requireChecked: true` refuses it as not ready.
 */
export function referralReferencesForRequest(
  rows: readonly AttachmentDraft[],
  options?: { requireChecked?: boolean },
): { ok: true; references: ReferralReference[] } | { ok: false; issues: ReferralReferenceIssue[] } {
  const requireChecked = options?.requireChecked !== false;
  const filled = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => !isBlank(row));
  const issues: ReferralReferenceIssue[] = [];
  if (filled.length > MAX_REFERENCES) {
    issues.push({ source: 'budget', code: 'too_many' });
  }

  const references: ReferralReference[] = [];
  let fileCount = 0;
  let totalBytes = 0;

  for (const { row, index } of filled) {
    if (row.kind === 'url') {
      const description = row.description.trim();
      const url = row.url.trim();
      if (description.length === 0 || url.length === 0) {
        issues.push({ source: 'url', index, code: 'incomplete' });
        continue;
      }
      let rowOk = true;
      if (description.length > REFERENCE_DESCRIPTION_MAX) {
        rowOk = false;
        issues.push({
          source: 'url',
          index,
          code: 'description_long',
          length: description.length,
          max: REFERENCE_DESCRIPTION_MAX,
        });
      }
      if (url.length > REFERENCE_URL_MAX) {
        rowOk = false;
        issues.push({ source: 'url', index, code: 'url_long', length: url.length, max: REFERENCE_URL_MAX });
      }
      if (!isLatin1(description) || !isLatin1(url)) {
        rowOk = false;
        issues.push({ source: 'url', index, code: 'latin1' });
      }
      if (rowOk && filled.length <= MAX_REFERENCES) {
        references.push({ kind: 'url', refDescription: description, url });
      }
      continue;
    }

    const description = row.description.trim();
    const fileName = row.fileName.trim();
    if (description.length === 0 || fileName.length === 0) {
      issues.push({ source: 'file', index, code: 'incomplete' });
      continue;
    }
    let rowOk = true;
    if (description.length > REFERENCE_DESCRIPTION_MAX) {
      rowOk = false;
      issues.push({
        source: 'file',
        index,
        code: 'description_long',
        length: description.length,
        max: REFERENCE_DESCRIPTION_MAX,
      });
    }
    if (!isLatin1(description)) {
      rowOk = false;
      issues.push({ source: 'file', index, code: 'latin1' });
    }
    const limit = checkReferralFile({
      fileName,
      byteLength: row.byteLength,
      existingFileCount: fileCount,
      existingTotalBytes: totalBytes,
    });
    if (!limit.ok) {
      rowOk = false;
      issues.push({ source: 'file', index, code: limit.code });
    } else {
      fileCount += 1;
      totalBytes += row.byteLength;
    }
    if (limit.ok && needsZipObjectType(fileName) && !row.zipObjectType) {
      rowOk = false;
      issues.push({ source: 'file', index, code: 'zip_type_missing' });
    }
    const objectIdentifier = parentObjectId(row.objectIdentifier);
    const size = row.originalObjectSize;
    const checked =
      objectIdentifier !== null &&
      row.objectCode.trim().length > 0 &&
      row.objectExtensionCode.trim().length > 0 &&
      size !== null &&
      Number.isInteger(size) &&
      size >= 0;
    if (requireChecked && !checked) {
      rowOk = false;
      issues.push({ source: 'file', index, code: 'not_ready' });
    }
    if (rowOk && filled.length <= MAX_REFERENCES && checked && objectIdentifier !== null && size !== null) {
      references.push({
        kind: 'bin',
        refDescription: description,
        objectIdentifier,
        objectCode: row.objectCode.trim(),
        objectExtensionCode: row.objectExtensionCode.trim(),
        originalObjectSize: size,
      });
    }
  }

  if (issues.length > 0) return { ok: false, issues };
  return { ok: true, references };
}
